import { byReference, type DashboardRow } from "../api/hooks/useDashboard";
import {
  type ActionListsData,
  daysSince,
  type NextAction,
  type ReadyCheck,
  type TrackedDate,
} from "../api/hooks/useProjectAgentStatus";
import { ADDRESS_RE } from "./gmailLinks";

/** The Overview's per-specialist counts over open actions: all of them, those on us
 * (an overdue reply's chase included), those waiting on someone outside, and those the
 * tables colour yellow (due soon) and red (overdue): our dates and the external deadlines
 * they serve. */
export const actionCounts = (actions: NextAction[], data: ActionListsData) => {
  const today = data.lists_today;
  const { dates } = projectItems(data);
  // An action dismissed until the next run leaves the counts until it comes back.
  const open = actions.filter((a) => !a.closed && !data.action_dismissals?.[a.action_id]);
  const tones = open.map((a) =>
    worstTone(
      dueTone(a, today),
      deadlineTone(a, a.serves ? dates.get(a.serves)?.date : undefined, today),
    ),
  );
  return {
    total: open.length,
    onUs: open.filter((a) => onWhom(a).side === "us").length,
    external: open.filter((a) => onWhom(a).side === "them").length,
    dueSoon: tones.filter((t) => t === "near").length,
    overdue: tones.filter((t) => t === "missed").length,
  };
};

/** A by-reference summary item's dashboard line, for the collapsed view. */
export const recordLine = (row: DashboardRow, id: string): string => {
  const action = row.records?.actions[id];
  return action ? action.title : id;
};

/** How each derived action status reads to an operator. */
export const STATUS_LABEL: Record<string, string> = {
  planned: "Planned",
  to_send: "To send",
  to_decide: "To decide",
  to_do: "To do",
  waiting: "Waiting",
  wait_failed: "Needs a look",
  awaiting_reply: "Awaiting reply",
  to_chase: "To chase",
  reply_received: "Reply received",
  answer_overdue: "Reply overdue",
  // Their reply overdue, as rows snapshotted before 13 Oct 2026 carry it.
  reply_overdue: "Reply overdue: chase",
  decided: "Decided",
  done: "Done",
  dropped: "Dropped",
};

/** What acts on an action, as its link names it: "→ email", "→ decision", "→ task". */
export const actionKind = (type: string): string =>
  type === "contact" ? "email" : type;

/** Hard: a counterparty, contract or statute sets it. Soft: one we track for ourselves. */
export const hardnessLabel = (hardness: string): string =>
  hardness === "hard" ? "External deadline" : "Internal tracking deadline";

/** How a row is coloured by its dates: "near" yellow, "missed" red. */
export type DateTone = "" | "near" | "missed";

// A deadline someone else sets, or a due date the record sets, warns two weeks out; our
// own target date warns five days out, since we can move it ourselves. Must agree with
// the dashboard row's counts (dashboard_rows.py).
export const NEAR_EXTERNAL_DAYS = 14;
export const NEAR_INTERNAL_DAYS = 5;

/** Whole days from ``today`` to ``iso``, both ISO dates; negative once it has passed. */
export const daysUntil = (iso: string, today: string): number =>
  Math.round(
    (Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) -
      Date.parse(`${today}T00:00:00Z`)) /
      86_400_000,
  );

/** How near a date is: within ``window`` days, or passed. */
export const toneFor = (
  iso: string,
  today: string,
  window: number,
): DateTone => {
  const days = daysUntil(iso, today);
  return days < 0 ? "missed" : days <= window ? "near" : "";
};

/** The date a row shows: for an email sent and not answered, when their reply is due,
 * which is also the day we chase; else the action's own due date - our answer's date
 * once theirs arrived. Must agree with ``shown_due`` in action_status.py. */
export const shownDue = (
  action: NextAction,
): { date: string; reply: boolean } => {
  const status = action.status?.status;
  const replyDue = action.status?.reply_due ?? "";
  return (status === "awaiting_reply" || status === "to_chase") && replyDue
    ? { date: replyDue, reply: true }
    : { date: action.due, reply: false };
};

/** A chase is red from the day their reply was due. A waiting action's own date is not in
 * force until what it waits on is done, so it takes no colour (Noaa, 28 Oct 2026). Must
 * agree with ``action_tone`` in dashboard_rows.py. */
export const dueTone = (action: NextAction, today: string): DateTone => {
  const { date, reply } = shownDue(action);
  return action.closed || !date || action.status?.status === "waiting"
    ? ""
    : action.status?.status === "to_chase"
      ? "missed"
      : toneFor(
        date,
        today,
        reply || action.due_basis === "target"
          ? NEAR_INTERNAL_DAYS
          : NEAR_EXTERNAL_DAYS,
      );
};

/** Only an external deadline colours the actions working towards it. */
export const deadlineTone = (
  action: NextAction,
  deadline: TrackedDate | undefined,
  today: string,
): DateTone =>
  action.closed ||
  !deadline ||
  deadline.closed ||
  deadline.hardness !== "hard" ||
  !deadline.date
    ? ""
    : toneFor(deadline.date, today, NEAR_EXTERNAL_DAYS);

export const worstTone = (...tones: DateTone[]): DateTone =>
  tones.includes("missed") ? "missed" : tones.includes("near") ? "near" : "";

export type ActionSort = "priority" | "due" | "deadline";

/** The external deadline an action works towards, as its row names it "deadline": a hard
 * tracked date still open. "" for none, a soft target or one not dated yet. */
export const hardDeadline = (deadline: TrackedDate | undefined): string =>
  deadline && !deadline.closed && deadline.hardness === "hard" ? deadline.date : "";

/** The Manager's ranking across specialists, action id to its place (1 first) and why it
 * ranks there ("" on a summary written before picks said why). */
export type ManagerRanking = ReadonlyMap<string, { place: number; why: string }>;

const NO_RANKING: ManagerRanking = new Map();

/** The Manager's ranking of every open action across the team. A summary written
 * before it ranked every action falls back to its blockers, then its next steps, as does
 * ``picksOnly``: a person's actions across projects lead with each project's picks, not
 * every project's #1, #2, ... (a far-off #3 above an overdue #25). Empty for a summary
 * from before summaries referenced records. */
export const managerRanking = (
  row: DashboardRow | undefined,
  { picksOnly = false }: { picksOnly?: boolean } = {},
): ManagerRanking => {
  const summary = row?.summary?.summary;
  if (!summary || !byReference(summary)) return NO_RANKING;
  if (summary.ranking && !picksOnly)
    return new Map(
      summary.ranking.map((entry, i) => [
        entry.action_id,
        { place: i + 1, why: entry.why },
      ]),
    );
  const ids = [...new Set([...summary.blockers, ...summary.next_steps])];
  return new Map(
    ids.map((id, i) => [
      id,
      { place: i + 1, why: row?.records?.actions[id]?.pick_reason ?? "" },
    ]),
  );
};

/** One project an action table reads from, with its Manager's ranking where the table
 * leads with it (null on a specialist's own table). */
export interface ActionSource {
  projectId: string;
  projectName: string;
  data: ActionListsData;
  manager: ManagerRanking | null;
}

/** One project's source for a specialist's own table. */
export const projectSource = (
  projectId: string,
  data: ActionListsData,
): ActionSource => ({ projectId, projectName: "", data, manager: null });

/** The Manager's picks still open, renumbered from 1: a pick closed since the summary was
 * written leaves no gap in the places the table shows. */
export const openPicks = (
  manager: ManagerRanking,
  items: ProjectItems,
): ManagerRanking => {
  const open = [...manager]
    .filter(([id]) => {
      const found = items.actions.get(id);
      return !!found && !found.action.closed;
    })
    .sort(([, a], [, b]) => a.place - b.place);
  return new Map(open.map(([id, pick], i) => [id, { ...pick, place: i + 1 }]));
};

/** Closed last, newest first; open by due date, undated (waiting) after dated. Sorted by
 * deadline, the earliest deadline (``deadlineOf``, :func:`hardDeadline`) leads, and those
 * with none or the same one go by due date. Sorted by priority, the Manager's ranking
 * leads (its picks by place, a tie between projects' picks by due date), then, with
 * ``ownRanking``, each specialist's own ranking (every #1, then every #2, ..., ties in
 * team order) and unranked blockers, then the rest. The owner chips name whoever holds
 * the first open action in one specialist's order, worked out by ``priority_key`` in
 * action_status.py, which must agree. */
export const compareActions = (
  sort: ActionSort,
  manager: ManagerRanking = NO_RANKING,
  {
    ownRanking = true,
    deadlineOf = () => "",
  }: { ownRanking?: boolean; deadlineOf?: (a: NextAction) => string } = {},
) => {
  const tier = (a: NextAction): number => {
    if (a.closed) return 4;
    if (sort !== "priority") return 3;
    if (manager.has(a.action_id)) return 0;
    if (!ownRanking) return 3;
    if (a.priority !== null && a.priority !== undefined) return 1;
    return a.blocking ? 2 : 3;
  };
  const byDue = (a: NextAction, b: NextAction): number =>
    (shownDue(a).date || "9999").localeCompare(shownDue(b).date || "9999");
  const byDeadline = (a: NextAction, b: NextAction): number =>
    sort === "deadline"
      ? (deadlineOf(a) || "9999").localeCompare(deadlineOf(b) || "9999")
      : 0;
  return (a: NextAction, b: NextAction): number => {
    const byTier = tier(a) - tier(b);
    if (byTier !== 0) return byTier;
    if (a.closed && b.closed) return b.closed.at.localeCompare(a.closed.at);
    if (tier(a) === 0)
      return (
        (manager.get(a.action_id)?.place ?? 0) -
          (manager.get(b.action_id)?.place ?? 0) || byDue(a, b)
      );
    if (tier(a) === 1) return (a.priority ?? 0) - (b.priority ?? 0);
    return byDeadline(a, b) || byDue(a, b);
  };
};

/** How long the row's oldest open blocking action has blocked, in days; -1 with none. */
export const oldestBlockerDays = (row: DashboardRow): number =>
  Math.max(
    -1,
    ...(row.records?.blocking ?? []).map(
      (b) => daysSince(b.blocking_since) ?? 0,
    ),
  );

export const TYPE_LABEL: Record<NextAction["type"], string> = {
  contact: "Email",
  decision: "Decision",
  task: "Task",
};

/** Who outside InRange a contact action asks, as "Name, Org". */
export const recipient = (action: NextAction): string => {
  const ext = action.external_owner;
  return ext ? `${ext.name}${ext.org ? `, ${ext.org}` : ""}` : "";
};

/** Who a wait names as having to act, as "Name, Org"; "" when the wait names nobody
 * (one written before waits named who). */
export const waitParty = (action: NextAction): string => {
  const who = action.waits_for?.who;
  return who?.name ? `${who.name}${who.org ? `, ${who.org}` : ""}` : "";
};

/** Who closed an action, as the opened row says it: the operator, or the agent. */
export const closedByLabel = (
  by: string,
  team: Record<string, string>,
  agentLabel: (agent: string) => string,
): string => {
  const match = /^agent:([a-z_]+)(?: \(review by (.+)\))?$/.exec(by);
  if (!match) return team[by] ?? by;
  const agent = `${agentLabel(match[1])} agent`;
  return match[2]
    ? `${agent}, applying ${team[match[2]] ?? match[2]}'s review`
    : agent;
};

/** ``text`` with each team member's email written as their name. */
export const namePeople = (
  text: string,
  team: Record<string, string>,
): string => text.replace(ADDRESS_RE, (email) => team[email] ?? email);

/** Every specialist's actions and dates on the project, by id, with whose they are. */
export interface ProjectItems {
  actions: Map<string, { agent: string; action: NextAction }>;
  dates: Map<string, { agent: string; date: TrackedDate }>;
  checks: Map<string, { agent: string; check: ReadyCheck }>;
}

export const projectItems = (data: ActionListsData): ProjectItems => {
  const actions = new Map<string, { agent: string; action: NextAction }>();
  const dates = new Map<string, { agent: string; date: TrackedDate }>();
  const checks = new Map<string, { agent: string; check: ReadyCheck }>();
  for (const entry of data.agents) {
    for (const check of entry.lists?.ready_checks ?? [])
      checks.set(check.check_id, { agent: entry.agent, check });
    for (const action of entry.lists?.actions ?? [])
      actions.set(action.action_id, { agent: entry.agent, action });
    for (const date of entry.lists?.tracked_dates ?? [])
      dates.set(date.date_id, { agent: entry.agent, date });
  }
  return { actions, dates, checks };
};

/** The latest date among the still-open items a waiting action waits on (an action's
 * shown date, a tracked date's date), or "" when none of them is dated: the earliest the
 * action can go ahead, which its Due column shows as "after <date>". */
export const waitUntil = (action: NextAction, items: ProjectItems): string => {
  const waits = action.waits_for;
  if (!waits) return "";
  const ids = waits.items.length > 0 ? waits.items.map((w) => w.item) : [waits.item];
  let latest = "";
  for (const id of ids) {
    const waited = items.actions.get(id)?.action;
    const tracked = items.dates.get(id)?.date;
    const date = waited
      ? waited.closed
        ? ""
        : shownDue(waited).date
      : tracked && !tracked.closed
        ? tracked.date
        : "";
    if (date > latest) latest = date;
  }
  return latest;
};

/** The checks an action works towards: the one it resolves, then the others the same
 * ask asks for (ready_checks.worked_checks). */
export const workedChecks = (action: NextAction): string[] =>
  action.resolves ? [action.resolves, ...(action.also_resolves ?? [])] : [];

export const worksTowards = (action: NextAction, checkId: string): boolean =>
  workedChecks(action).includes(checkId);

/** The first checklist item an open action works towards that is its own specialist's,
 * open and unmet: as ready_checks.unlinked_actions reads it, so the page and the pass gate
 * agree on which actions have none. */
export const workedCheck = (
  items: ProjectItems,
  action: NextAction,
): ReadyCheck | undefined => {
  const owner = items.actions.get(action.action_id)?.agent;
  for (const id of workedChecks(action)) {
    const entry = items.checks.get(id);
    const check = entry && entry.agent === owner ? entry.check : undefined;
    if (check && !check.closed && !check.met) return check;
  }
  return undefined;
};

/** What a dependency line says of one item on the project, any specialist's: its title,
 * the anchor it links to on its specialist's page, its date (null for a Ready to build
 * check, which has none), who owns it and where it stands. Undefined once it is gone. */
export const itemFacts = (
  id: string,
  items: ProjectItems,
  team: Record<string, string>,
):
  | {
      agent: string;
      anchor: string;
      title: string;
      when: string | null;
      owner: string;
      state: string;
    }
  | undefined => {
  const action = items.actions.get(id);
  if (action)
    return {
      agent: action.agent,
      anchor: id,
      title: action.action.title,
      when: action.action.due,
      owner: team[action.action.internal_owner] ?? "",
      state: action.action.closed
        ? (STATUS_LABEL[action.action.closed.outcome] ??
          action.action.closed.outcome)
        : statusLabel(action.action),
    };
  const date = items.dates.get(id);
  if (date)
    return {
      agent: date.agent,
      anchor: id,
      title: date.date.what,
      when: date.date.date,
      owner: "",
      state: date.date.closed?.outcome ?? "Open",
    };
  const check = items.checks.get(id);
  if (check)
    return {
      agent: check.agent,
      anchor: "ready-to-build",
      title: check.check.text,
      when: null,
      owner: "",
      state: check.check.closed
        ? check.check.closed.outcome
        : check.check.met
          ? "Met"
          : "Not met",
    };
  return undefined;
};

/** Whether an item on the project has finished: an action or tracked date closed, a check
 * met or closed. An item the page does not carry (a specialist not on the project) counts
 * as still open, so the page never ends a wait the agent does not. */
export const itemFinished = (items: ProjectItems, id: string): boolean => {
  const action = items.actions.get(id);
  if (action) return !!action.action.closed;
  const date = items.dates.get(id);
  if (date) return !!date.date.closed;
  const other = items.checks.get(id);
  if (other) return !!other.check.closed || other.check.met;
  return false;
};

/** A check waiting on items that have all closed or been met: nothing it names moves it
 * now (ready_checks.wait_ended). */
export const waitEnded = (check: ReadyCheck, items: ProjectItems): boolean =>
  check.waits_for.length > 0 &&
  check.waits_for.every((id) => itemFinished(items, id));

/** The open actions of a check's own specialist working towards it. */
export const resolvingActions = (
  items: ProjectItems,
  checkId: string,
): NextAction[] => {
  const owner = items.checks.get(checkId)?.agent;
  return [...items.actions.values()]
    .filter(
      ({ agent, action }) =>
        agent === owner && !action.closed && worksTowards(action, checkId),
    )
    .map(({ action }) => action);
};

/** Where a check stands: met, worked by open actions, waiting with a reason, waiting on
 * items that have all finished, or with nobody on it. */
export type CheckState = "met" | "worked" | "waiting" | "wait_ended" | "nobody";

export const checkState = (
  check: ReadyCheck,
  items: ProjectItems,
): CheckState => {
  if (check.met) return "met";
  if (resolvingActions(items, check.check_id).length > 0) return "worked";
  if (!check.no_action_reason) return "nobody";
  return waitEnded(check, items) ? "wait_ended" : "waiting";
};

/** How each check state reads on its chip. */
export const CHECK_STATE_LABEL: Record<CheckState, string> = {
  met: "Met",
  worked: "Being worked",
  waiting: "Waiting",
  wait_ended: "Wait ended",
  nobody: "Nobody on it",
};

/** What an unmet check with no action and no reason says when opened. */
export const NO_ACTION_NO_REASON =
  "No open action resolves this, and no reason is given why none is needed.";

/** The open actions working towards a tracked date, any specialist's. */
export const servingActions = (
  items: ProjectItems,
  dateId: string,
): { agent: string; action: NextAction }[] =>
  [...items.actions.values()].filter(
    ({ action }) => !action.closed && action.serves === dateId,
  );

/** What kind of action it is, and who an email goes to. */
export const typeLine = (action: NextAction): string =>
  action.type === "contact"
    ? `Email to ${recipient(action)}`
    : TYPE_LABEL[action.type];

/** The owner's name, from the team list. */
export const ownerName = (
  team: Record<string, string>,
  action: NextAction,
): string => team[action.internal_owner] ?? action.internal_owner;

/** A specialist's open actions in the Priority order its page's table uses. */
export const prioritised = (actions: NextAction[]): NextAction[] =>
  actions.filter((a) => !a.closed).sort(compareActions("priority"));

/** Who has to act on an action now. An email awaiting a reply is on the person it went
 * to until the reply is overdue; then the chase is ours, overdue, as the backend's
 * ``ball`` says. Our own answer overdue is ours and overdue too. */
export interface OnWhom {
  side: "us" | "them" | "";
  /** "Us", or who outside InRange: the email's recipient, or the event a wait names. */
  party: string;
  overdue: boolean;
}

export const onWhom = (action: NextAction): OnWhom => {
  const status = action.status?.status ?? "";
  // Planned and decided are held by nobody, as the backend's ``ball`` says.
  if (action.closed || !status || status === "planned" || status === "decided")
    return { side: "", party: "", overdue: false };
  // reply_overdue: their reply overdue, as rows snapshotted before 13 Oct 2026 carry it.
  if (
    status === "to_chase" ||
    status === "answer_overdue" ||
    status === "reply_overdue"
  )
    return { side: "us", party: "Us", overdue: true };
  if (status === "awaiting_reply")
    return { side: "them", party: recipient(action) || "Them", overdue: false };
  if (status === "waiting" && action.waits_for?.on === "them")
    return { side: "them", party: waitParty(action) || "Them", overdue: false };
  return { side: "us", party: "Us", overdue: false };
};

export const statusLabel = (action: NextAction): string =>
  STATUS_LABEL[action.status?.status ?? ""] ?? action.status?.status ?? "";

/** The date a row shows, whether it is our own target, and what hovering it says. */
export const dueHint = (action: NextAction) => {
  const { date, reply } = shownDue(action);
  const target = !reply && action.due_basis === "target";
  return {
    date,
    target,
    title: reply
      ? "When their reply is due"
      : target
        ? "Our own target date, not one set by someone outside"
        : undefined,
  };
};

export type PartTone = "" | "near" | "missed" | "closed" | "blocking";

/** The status, Blocking, and how near the external deadline it serves is: the words the
 * status chips show, each with its colour. */
export const statusParts = (
  action: NextAction,
  deadline: TrackedDate | undefined,
  today: string,
): { text: string; tone: PartTone }[] => {
  const toward = deadlineTone(action, deadline, today);
  const days = deadline?.date ? daysUntil(deadline.date, today) : 0;
  const parts: { text: string; tone: PartTone }[] = [];
  if (action.status)
    parts.push({
      text: statusLabel(action),
      tone: action.closed ? "closed" : action.status.late ? "missed" : "",
    });
  if (action.blocking && !action.closed)
    parts.push({ text: "Blocking", tone: "blocking" });
  if (toward === "missed")
    parts.push({ text: "Deadline missed", tone: "missed" });
  if (toward === "near")
    parts.push({
      text:
        days === 0
          ? "Deadline today"
          : `Deadline in ${days} day${days === 1 ? "" : "s"}`,
      tone: "near",
    });
  return parts;
};

/** A decision's numbered options, ``{number: its line}``, as its call to action words them.
 * Must agree with ``option_lines`` in next_actions.py. */
export const optionLines = (callToAction: string): Map<number, string> =>
  new Map(
    callToAction
      .split("\n")
      .map((line) => /^\s*(\d+)\.\s+(.*)$/.exec(line))
      .filter((match): match is RegExpExecArray => match !== null)
      .map((match) => [Number(match[1]), match[2].trim()]),
  );

/** ``days`` calendar days on from the due date, or from today once it has passed, moved
 * off a weekend to the Monday. The server moves one on a bank holiday too. */
export const pushTarget = (
  due: string,
  today: string,
  days: number,
): string => {
  const from = due && due >= today ? due : today;
  const when = new Date(`${from}T00:00:00Z`);
  when.setUTCDate(when.getUTCDate() + days);
  while (when.getUTCDay() === 0 || when.getUTCDay() === 6)
    when.setUTCDate(when.getUTCDate() + 1);
  return when.toISOString().slice(0, 10);
};

/** The server's limit on a resolve's reason (next_actions.apply_action_changes). */
export const RESOLVE_REASON_MAX = 300;

/** Why an open action's date cannot be pushed by the operator, or null when it can: one
 * waiting on something takes its date from that, and an agent's open proposal to move it
 * is answered rather than overtaken. Its own row and a bulk push both ask this. */
export const pushBlocker = (
  action: NextAction,
  dateMoveOpen: boolean,
): string | null => {
  if (action.closed) return "already resolved";
  if (!action.due) return "has no due date";
  if (action.waits_for) return "waits on something else, which sets its date";
  if (dateMoveOpen) return "has a date move to answer first";
  return null;
};

/** A decision written with numbered options says which was taken when it is done. */
export const needsOptionChoice = (
  action: NextAction,
  outcome: "done" | "dropped",
): boolean =>
  action.type === "decision" &&
  outcome === "done" &&
  optionLines(action.call_to_action).size > 0;

/** What each item a wait names must close with, as the opened row says it. */
export const waitCondition = (
  needs: {
    kind: string;
    options: number[];
    labels: string[];
    outcomes: string[];
  },
  owner: string,
): string => {
  if (needs.kind === "options")
    return `Goes ahead only if ${owner || "the decision"} chooses: ${needs.options
      .map((n, i) => needs.labels[i] || `option ${n}`)
      .join(" or ")}`;
  if (needs.kind === "outcomes")
    return `Goes ahead once it is ${needs.outcomes.join(" or ")}`;
  if (needs.kind === "closed")
    return "Goes ahead once it closes, however it closes";
  return "Goes ahead once it's done";
};

/** The items a wait names, reading a wait written before 13 Oct 2026 (one ``item``,
 * ended however it closed) as one. Must agree with ``wait_items`` in next_actions.py. */
export const waitItems = (action: NextAction) => {
  const waits = action.waits_for;
  if (!waits) return [];
  if (waits.items?.length) return waits.items;
  return waits.item
    ? [
        {
          item: waits.item,
          needs: {
            kind: "closed" as const,
            options: [],
            labels: [],
            outcomes: [],
          },
        },
      ]
    : [];
};
